import { useRouter } from '@tanstack/react-router';
import { useRegisterBreadcrumbs } from '@/services/breadcrumbs';
import { generatePath } from '@/services/router/generate-path';
import type { SqlDatabaseType } from '@/services/db/sql-import-database/entity';
import { SQL_DATABASE_CATALOG } from '@/features/sql-sync';
import { isEmptyString } from '@/utils/string';

/**
 * The Integrations crumb points at the Ember URL so it resolves in both flag states — that page
 * has not migrated, and the `journeys` twin is the shape Ember serves.
 */
const EMBER_INTEGRATIONS_PATH = '/workspaces/:workspaceId/journeys/integrations';

export const SQL_SYNC_LIST_ROUTE_PATHS = {
  migrated: '/workspaces/$workspaceId/integrations/sql-sync/$type',
  legacy: '/workspaces/$workspaceId/journeys/integrations/sql-sync/$type',
} as const;

export const SQL_SYNC_NEW_ROUTE_PATHS = {
  migrated: '/workspaces/$workspaceId/integrations/sql-sync/$type/new',
  legacy: '/workspaces/$workspaceId/journeys/integrations/sql-sync/$type/new',
} as const;

export const SQL_SYNC_IMPORT_ROUTE_PATHS = {
  migrated: '/workspaces/$workspaceId/integrations/sql-sync/$type/$sqlImportId',
  legacy: '/workspaces/$workspaceId/journeys/integrations/sql-sync/$type/$sqlImportId',
} as const;

/** The detail's editable tabs; the Imports tab is the detail's index and has no segment of its own. */
export const SQL_SYNC_IMPORT_TABS = ['query', 'relationships-query', 'settings'] as const;
export type SqlSyncImportTab = (typeof SQL_SYNC_IMPORT_TABS)[number];

export const SQL_SYNC_IMPORT_TAB_ROUTE_PATHS = {
  query: {
    migrated: '/workspaces/$workspaceId/integrations/sql-sync/$type/$sqlImportId/query',
    legacy: '/workspaces/$workspaceId/journeys/integrations/sql-sync/$type/$sqlImportId/query',
  },
  'relationships-query': {
    migrated: '/workspaces/$workspaceId/integrations/sql-sync/$type/$sqlImportId/relationships-query',
    legacy: '/workspaces/$workspaceId/journeys/integrations/sql-sync/$type/$sqlImportId/relationships-query',
  },
  settings: {
    migrated: '/workspaces/$workspaceId/integrations/sql-sync/$type/$sqlImportId/settings',
    legacy: '/workspaces/$workspaceId/journeys/integrations/sql-sync/$type/$sqlImportId/settings',
  },
} as const satisfies Record<SqlSyncImportTab, { migrated: string; legacy: string }>;

/** The detail tab a pathname ends in, or null on the detail itself (the Imports tab). */
export function sqlSyncImportTabFromPathname(pathname: string): SqlSyncImportTab | null {
  const last = pathname.replace(/\/+$/, '').split('/').pop();
  return SQL_SYNC_IMPORT_TABS.find((tab) => tab === last) ?? null;
}

/** Both spellings of one detail URL: the detail itself, or one of its tabs. */
export function sqlSyncImportRoutePaths(tab: SqlSyncImportTab | null): { migrated: string; legacy: string } {
  return tab == null ? SQL_SYNC_IMPORT_ROUTE_PATHS : SQL_SYNC_IMPORT_TAB_ROUTE_PATHS[tab];
}

type Router = ReturnType<typeof useRouter>;

/** "Integrations / MySQL": the crumbs every SQL sync page starts with, as the Ember routes registered them. */
function sqlSyncBaseCrumbs(router: Router, workspaceId: string, type: SqlDatabaseType) {
  return [
    {
      id: 'integrations',
      text: 'Integrations',
      href: generatePath(EMBER_INTEGRATIONS_PATH, { workspaceId }),
    },
    {
      id: 'integrations_sql_sync',
      text: SQL_DATABASE_CATALOG[type].name,
      href: router.buildLocation({ to: SQL_SYNC_LIST_ROUTE_PATHS.migrated, params: { workspaceId, type } }).href,
    },
  ];
}

/** "Integrations / MySQL", as the Ember routes registered them. */
export function useSqlSyncListBreadcrumbs(workspaceId: string, type: SqlDatabaseType): void {
  const router = useRouter();

  useRegisterBreadcrumbs([...sqlSyncBaseCrumbs(router, workspaceId, type)]);
}

/** "Integrations / MySQL / Create Sync" on the create wizard. */
export function useSqlSyncNewBreadcrumbs(workspaceId: string, type: SqlDatabaseType): void {
  const router = useRouter();

  useRegisterBreadcrumbs([
    ...sqlSyncBaseCrumbs(router, workspaceId, type),
    {
      id: 'integrations_sql_sync_new',
      text: 'Create Sync',
      href: router.buildLocation({ to: SQL_SYNC_NEW_ROUTE_PATHS.migrated, params: { workspaceId, type } }).href,
    },
  ]);
}

/** "Integrations / MySQL / <import name>" on the import detail, as Ember's `sql-imports.import` crumb read. */
export function useSqlSyncImportBreadcrumbs(
  workspaceId: string,
  type: SqlDatabaseType,
  sqlImportId: string,
  name: string,
): void {
  const router = useRouter();

  useRegisterBreadcrumbs([
    ...sqlSyncBaseCrumbs(router, workspaceId, type),
    {
      id: 'integrations_sql_sync_import',
      text: isEmptyString(name) ? 'SQL Sync' : name,
      href: router.buildLocation({
        to: SQL_SYNC_IMPORT_ROUTE_PATHS.migrated,
        params: { workspaceId, type, sqlImportId },
      }).href,
    },
  ]);
}
